import React from "react";
import { Helmet } from "react-helmet";
import "./LandingPage.css";
import Navbar from "../UI/Navbar";
import Footer from "../UI/Footer";
import ImageCarousel from "../UI/ImageCarousel";
import { useTranslation } from "react-i18next";

const LinkedinHeadshotPage = () => {
  // Utilisation du hook useTranslation pour accéder aux traductions
  const { t, i18n } = useTranslation('linkedin');
  const currentLang = i18n.language;

  // Images spécifiques pour le carousel LinkedIn
  const linkedinImages = [
    '/images/li_caroussel/avatar1.webp',
    '/images/li_caroussel/avatar2.webp',
    '/images/li_caroussel/avatar3.webp',
    '/images/li_caroussel/avatar4.webp',
    '/images/li_caroussel/avatar5.webp',
    '/images/li_caroussel/avatar6.webp',
    '/images/li_caroussel/avatar7.webp',
    '/images/li_caroussel/avatar8.webp',
    '/images/li_caroussel/avatar9.webp',
    '/images/li_caroussel/avatar10.webp',
    '/images/li_caroussel/avatar11.webp',
    '/images/li_caroussel/avatar12.webp',
    '/images/li_caroussel/avatar13.webp',
    '/images/li_caroussel/avatar14.webp',
    '/images/li_caroussel/avatar15.webp',
    '/images/li_caroussel/avatar16.webp',
    '/images/li_caroussel/avatar17.webp',
    '/images/li_caroussel/avatar18.webp',
    '/images/li_caroussel/avatar19.webp',
    '/images/li_caroussel/avatar20.webp',
    '/images/li_caroussel/avatar21.webp',
    '/images/li_caroussel/avatar22.webp',
    '/images/li_caroussel/avatar23.webp',
    '/images/li_caroussel/avatar24.webp',
    '/images/li_caroussel/avatar25.webp',
    '/images/li_caroussel/avatar26.webp',
    '/images/li_caroussel/avatar27.webp',
    '/images/li_caroussel/avatar28.webp',
    '/images/li_caroussel/avatar29.webp',
    '/images/li_caroussel/avatar30.webp'
    
  ];

  /* -------------------- Structured Data -------------------- */
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "AiVatar - AI LinkedIn Headshot Generator",
    description:
      "Create professional LinkedIn profile pictures from selfies in 30 seconds. Studio quality, instant downloads, commercial-use ready.",
    image: "https://aivatar.muware.xyz/og-image-linkedin.webp",
    brand: {
      "@type": "Brand",
      name: "AiVatar"
    },
    aggregateRating: {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "4800"
    },
    offers: [
      {
        "@type": "Offer",
        price: "9",
        priceCurrency: "EUR",
        priceValidUntil: "2025-12-31",
        availability: "https://schema.org/InStock",
        name: "40-token lifetime pack",
        description: "One-off pack of 40 tokens (enough for 10-20 LinkedIn headshots)."
      },
      {
        "@type": "Offer",
        price: "9",
        priceCurrency: "EUR",
        priceValidUntil: "2025-12-31",
        availability: "https://schema.org/InStock",
        name: "Monthly subscription - 100 tokens/month",
        description: "Renews every month with 100 fresh tokens for LinkedIn headshots.",
        eligibleDuration: "P1M"
      }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: t('faq.items.guidelines.q'),
        acceptedAnswer: {
          "@type": "Answer",
          text: t('faq.items.guidelines.a')
        }
      },
      {
        "@type": "Question",
        name: t('faq.items.watermark.q'),
        acceptedAnswer: {
          "@type": "Answer",
          text: t('faq.items.watermark.a')
        }
      },
      {
        "@type": "Question",
        name: t('faq.items.expire.q'),
        acceptedAnswer: {
          "@type": "Answer",
          text: t('faq.items.expire.a')
        }
      },
      {
        "@type": "Question",
        name: t('faq.items.free.q'),
        acceptedAnswer: {
          "@type": "Answer",
          text: t('faq.items.free.a')
        }
      },
      {
        "@type": "Question",
        name: t('faq.items.better.q'),
        acceptedAnswer: {
          "@type": "Answer",
          text: t('faq.items.better.a')
        }
      }
    ]
  };

  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: "AiVatar LinkedIn Headshot Generator Demo",
    description: "Watch how to create a professional LinkedIn headshot from selfies in under a minute with AiVatar.",
    thumbnailUrl: "https://i.ytimg.com/vi_webp/XXXXXXXXXXX/maxresdefault.webp",
    uploadDate: "2025-05-12T08:00:00+00:00",
    contentUrl: "https://www.youtube.com/watch?v=XXXXXXXXXXX",
    embedUrl: "https://www.youtube.com/embed/XXXXXXXXXXX",
    duration: "PT0M59S"
  };

  return (
    <div className="landing-page">
      <Navbar />

      {/* Ajout du carrousel d'images spécifique à LinkedIn */}
      <ImageCarousel images={linkedinImages} />

      <Helmet>
        <title>{t('seo.title')}</title>
        <meta
          name="description"
          content={t('seo.description')}
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={`https://aivatar.muware.xyz${currentLang === 'fr' ? '/fr' : ''}/linkedin-headshot`} />
        
        {/* Alternate language versions for hreflang */}
        <link
          rel="alternate"
          href="https://aivatar.muware.xyz/linkedin-headshot"
          hrefLang="en"
        />
        <link
          rel="alternate"
          href="https://aivatar.muware.xyz/fr/linkedin-headshot"
          hrefLang="fr"
        />
        
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(videoSchema)}</script>
        
        {/* Open Graph / Twitter Card */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t('seo.title')} />
        <meta property="og:description" content={t('seo.description')} />
        <meta property="og:image" content="https://aivatar.muware.xyz/og-image-linkedin.webp" />
        <meta property="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* ---------------- HERO SECTION ---------------- */}
      <section className="landing-page__hero section container text-center">
        <h1 className="mb-4 mx-auto" style={{ maxWidth: "800px" }}>
          {t('hero.title')}
        </h1>
        <p
          className="landing-page__subtitle mb-5 mx-auto"
          style={{ maxWidth: "600px" }}
          dangerouslySetInnerHTML={{ __html: t('hero.subtitle') }}
        />
        <a href={`/${currentLang === 'fr' ? 'fr/' : ''}signup`} className="btn btn-primary btn-xl">
          {t('hero.button')}
        </a>
      </section>

      {/* SOCIAL PROOF (metrics) */}
      <section className="landing-page__social-proof text-center">
        <div className="container">
          <span>{t('socialProof.renderTime')}</span>
          <span>{t('socialProof.resolution')}</span>
          <span>{t('socialProof.privacy')}</span>
        </div>
      </section>

      {/* PAIN + PROMISE SECTION */}
      <section className="landing-page__section-container">
        <div className="landing-page__panel section">
          <h2 className="text-center mb-5">{t('painPromise.title')}</h2>
          <div className="flex flex-col items-center">
            <p className="text-center mb-5"
               dangerouslySetInnerHTML={{ __html: t('painPromise.subtitle') }}
            />
            <div className="linkedin-comparison w-full max-w-4xl mx-auto">
              {/* Placeholder for before/after comparison image */}
              <div className="comparison-placeholder text-center p-8 bg-gray-50 rounded-lg">
                <p className="text-lg font-medium mb-2">Before/After Comparison Image</p>
                <p className="text-muted">{t('painPromise.comparison')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="landing-page__section-container">
        <div className="landing-page__how-it-works section text-center">
          <h2 className="mb-6">{t('howItWorks.title')}</h2>
          <div className="landing-page__steps-grid">
            {[
              {
                step: "1",
                title: t('howItWorks.step1.title'),
                text: t('howItWorks.step1.text')
              },
              {
                step: "2",
                title: t('howItWorks.step2.title'),
                text: t('howItWorks.step2.text')
              },
              {
                step: "3",
                title: t('howItWorks.step3.title'),
                text: t('howItWorks.step3.text')
              }
            ].map((item, i) => (
              <div key={i} className="landing-page__step-card">
                <div className="step-number">{item.step}</div>
                <h3 className="font-bold">{item.title}</h3>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STYLE GALLERY */}
      <section className="landing-page__section-container">
        <div className="landing-page__examples-section">
          <h2 className="text-center mb-6">{t('styleGallery.title')}</h2>
          <p className="landing-page__examples-subtitle text-center mb-6">
            {t('styleGallery.subtitle')}
          </p>
          <div className="landing-page__popular-styles-grid">
            {[
              { src: "/images/LinkedIn/aivatar_CreativeDirector.webp", alt: "AI LinkedIn Profile Picture - Creative Director" },
              { src: "/images/LinkedIn/aivatar_FinancialStrategist.webp", alt: "AI LinkedIn Profile Picture - Financial Strategist" },
              { src: "/images/LinkedIn/aivatar_InternationalConsultant.webp", alt: "AI LinkedIn Profile Picture - International Consultant" },
              { src: "/images/LinkedIn/aivatar_PublicSpeaker.webp", alt: "AI LinkedIn Profile Picture - Public Speaker" },
              { src: "/images/LinkedIn/aivatar_StartupVisionary.webp", alt: "AI LinkedIn Profile Picture - Startup Visionary" },
              { src: "/images/LinkedIn/aivatar_SustainabilityAdvisor.webp", alt: "AI LinkedIn Profile Picture - Sustainability Advisor" },
              { src: "/images/LinkedIn/aivatar_TechMinimalist.webp", alt: "AI LinkedIn Profile Picture - Tech Minimalist" },
              { src: "/images/LinkedIn/aivatar_UXResearcher.webp", alt: "AI LinkedIn Profile Picture - UX Researcher" }
            ].map((style, idx) => (
              <figure key={idx} className="style-card">
                <div className="style-image-container">
                  <img 
                    src={style.src} 
                    alt={style.alt}
                    className="style-image"
                    loading="lazy"
                  />
                </div>
                <figcaption className="style-caption">{style.alt.split("- ")[1]}</figcaption>
              </figure>
            ))}
          </div>
        </div>

        <style jsx>{`
          .landing-page__popular-styles-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 2rem;
            padding: 1rem;
          }

          .style-card {
            display: flex;
            flex-direction: column;
            background: #fff;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            transition: transform 0.2s ease;
            height: 100%;
          }

          .style-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          }

          .style-image-container {
            position: relative;
            padding-top: 125%; /* Ratio 4:5 pour un format portrait uniforme */
            width: 100%;
            background: #f5f5f5;
          }

          .style-image {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center top;
          }

          .style-caption {
            padding: 1rem;
            text-align: center;
            font-weight: 500;
            color: #333;
            background: #fff;
            margin: 0;
          }

          @media (max-width: 1024px) {
            .landing-page__popular-styles-grid {
              grid-template-columns: repeat(2, 1fr);
              gap: 1.5rem;
            }
          }

          @media (max-width: 640px) {
            .landing-page__popular-styles-grid {
              grid-template-columns: 1fr;
              gap: 1rem;
            }
          }
        `}</style>
      </section>

      {/* FEATURES GRID */}
      <section className="landing-page__section-container">
        <div className="landing-page__features-section">
          <h2 className="text-center mb-6">{t('features.title')}</h2>
          <div className="landing-page__features-grid">
            {[
              {
                title: t('features.items.quality.title'),
                text: t('features.items.quality.text')
              },
              {
                title: t('features.items.license.title'),
                text: t('features.items.license.text')
              },
              {
                title: t('features.items.diversity.title'),
                text: t('features.items.diversity.text')
              },
              {
                title: t('features.items.privacy.title'),
                text: t('features.items.privacy.text')
              },
              {
                title: t('features.items.optimized.title'),
                text: t('features.items.optimized.text')
              },
              {
                title: t('features.items.approved.title'),
                text: t('features.items.approved.text')
              }
            ].map((f, i) => (
              <div key={i} className="landing-page__feature-card text-center p-5">
                <h3 className="font-bold mb-3">{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    

      {/* PRICING TEASER */}
      <section className="landing-page__section-container">
        <div className="landing-page__pricing-section text-center py-16">
          <h2 className="mb-4">{t('pricing.title')}</h2>
          <p className="mb-5">{t('pricing.subtitle')}</p>
          <a href={`/${currentLang === 'fr' ? 'fr/' : ''}pricing`} className="btn btn-outline btn-lg">
            {t('pricing.button')}
          </a>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="landing-page__section-container">
        <div className="landing-page__faq-section">
          <h2 className="text-center mb-6">{t('faq.title')}</h2>
          <div className="space-y-4 mx-auto" style={{ maxWidth: "800px" }}>
            {[
              {
                q: t('faq.items.guidelines.q'),
                a: t('faq.items.guidelines.a')
              },
              {
                q: t('faq.items.watermark.q'),
                a: t('faq.items.watermark.a')
              },
              {
                q: t('faq.items.expire.q'),
                a: t('faq.items.expire.a')
              },
              {
                q: t('faq.items.free.q'),
                a: t('faq.items.free.a')
              },
              {
                q: t('faq.items.better.q'),
                a: t('faq.items.better.a')
              }
            ].map((item, i) => (
              <details key={i} className="p-4 border rounded-lg">
                <summary className="font-semibold cursor-pointer">{item.q}</summary>
                <p className="mt-2">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="landing-page__section-container">
        <div className="landing-page__final-cta-section text-center">
          <h2 className="mx-auto mb-5" style={{ maxWidth: "800px" }}>
            {t('finalCta.title')}
          </h2>
          <a href={`/${currentLang === 'fr' ? 'fr/' : ''}signup`} className="btn btn-secondary btn-xl">
            {t('finalCta.button')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LinkedinHeadshotPage; 